/**
 * Workshop version of ChatFrame — WhatsApp-style message thread.
 *
 * Production frame recipe inside (conversation-frame bubbles verbatim),
 * Denker glass outside. Content area has no divider lines, no status
 * dots, and no follow-up prompts.
 *
 * Props-in, JSX-out. Local UI state only (error-banner dismiss).
 */

import { useState } from "react";
import { Icons } from "@/components/ui/icons";
import { surfaceRoleAttributes, surfaceRoleClassName } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface ChatFrameMessage {
  id: string;
  from?: string;
  mine: boolean;
  text: string;
  time: string;
  read?: boolean;
}

interface ChatFrameProps {
  messages?: ChatFrameMessage[];
  status?: FrameStatus;
  className?: string;
  "data-testid"?: string;
}

const DEFAULT_MESSAGES: ChatFrameMessage[] = [
  {
    id: "c1",
    from: "Mara",
    mine: false,
    text: "Launch checklist is green except the App Store screenshots — still waiting on design.",
    time: "09:41",
  },
  {
    id: "c2",
    mine: true,
    text: "I'll chase them this morning. Can we still submit tomorrow if they land by EOD?",
    time: "09:43",
    read: true,
  },
  {
    id: "c3",
    from: "Jonas",
    mine: false,
    text: "Yes — review usually takes under 24h for updates. Screenshots are the only blocker.",
    time: "09:45",
  },
  {
    id: "c4",
    mine: true,
    text: "Perfect, submitting first thing tomorrow then.",
    time: "09:46",
    read: false,
  },
];

export function ChatFrame({
  messages = DEFAULT_MESSAGES,
  status = "done",
  className,
  "data-testid": testId = "chat-frame",
}: ChatFrameProps) {
  const [errorDismissed, setErrorDismissed] = useState(false);
  const showError = status === "error" && !errorDismissed;
  const isEmpty = messages.length === 0;

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex flex-col overflow-hidden rounded-frame",
        className,
      )}
      {...surfaceRoleAttributes("frame", { nativeLevel: "root" })}
      data-testid={testId}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-2.5 py-2">
        {showError ? (
          <div
            className="flex flex-col gap-1.5 rounded-lg border border-danger/20 bg-danger-muted px-2.5 py-2"
            data-testid={`${testId}-error`}
          >
            <div className="flex items-center gap-1.5">
              <Icons.XCircle className="h-3.5 w-3.5 shrink-0 text-danger" />
              <span className="text-canvas-sm font-medium text-danger">
                Couldn&apos;t load thread
              </span>
            </div>
            <p className="text-canvas-sm leading-relaxed text-secondary">
              The conversation history failed to sync. Your follow-up prompts
              below still work.
            </p>
            <button
              type="button"
              onClick={() => setErrorDismissed(true)}
              className="self-start rounded-full border border-glass-stroke-subtle bg-glass-fill px-2 py-0.5 text-canvas-xs text-muted transition-colors hover:text-secondary"
              data-testid={`${testId}-error-dismiss`}
            >
              Dismiss
            </button>
          </div>
        ) : isEmpty ? (
          status === "streaming" ? (
            <div
              className="flex justify-start"
              data-testid={`${testId}-typing`}
            >
              <div className="flex items-center gap-1 rounded-preview bg-elevated/60 px-3 py-2.5">
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted"
                    aria-hidden="true"
                  />
                ))}
                <span className="sr-only">Loading messages</span>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-1 py-6"
              data-testid={`${testId}-empty`}
            >
              <Icons.MessageCircle className="h-5 w-5 text-subtle" />
              <p className="text-canvas-sm italic text-muted">
                No messages yet
              </p>
            </div>
          )
        ) : (
          messages.map((msg, idx) => {
            const isLast = idx === messages.length - 1;
            return (
              <div
                key={msg.id}
                className={cn(
                  "flex w-full",
                  msg.mine ? "justify-end" : "justify-start",
                )}
                data-testid={`${testId}-msg-${msg.id}`}
              >
                {msg.mine ? (
                  <div className="max-w-[84%] whitespace-pre-wrap rounded-preview bg-accent/18 px-3 py-2 text-canvas-base leading-relaxed text-primary">
                    <p className="break-words">
                      {msg.text}
                      {isLast && status === "streaming" && (
                        <span
                          className="ml-1 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
                          aria-label="Streaming"
                        />
                      )}
                    </p>
                    <div className="mt-0.5 flex items-center justify-end gap-1">
                      <span className="text-canvas-xs text-muted">
                        {msg.time}
                      </span>
                      <Icons.CheckCheck
                        className={cn(
                          "h-3 w-3",
                          msg.read ? "text-accent" : "text-subtle",
                        )}
                        aria-label={msg.read ? "Read" : "Sent"}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="max-w-[84%] whitespace-pre-wrap rounded-preview bg-elevated/60 px-3 py-2 text-canvas-base leading-relaxed text-primary">
                    {msg.from && (
                      <p className="text-canvas-2xs font-semibold text-accent">
                        {msg.from}
                      </p>
                    )}
                    <p className="break-words">
                      {msg.text}
                      {isLast && status === "streaming" && (
                        <span
                          className="ml-1 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
                          aria-label="Streaming"
                        />
                      )}
                    </p>
                    <div className="mt-0.5 flex items-center justify-end gap-1">
                      <span className="text-canvas-xs text-muted">
                        {msg.time}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export type { ChatFrameProps, ChatFrameMessage, FrameStatus as ChatFrameStatus };
